import { useState } from 'react';
import cn from 'classnames';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiDeleteReview } from 'api';
import { Review } from 'types';
import { TrashIcon } from '../../icons';
import { useAuth } from '../../contexts/auth-context';
import { StarRating } from '../star-rating';
import { EditButton } from '../edit-button';
import { ReviewForm } from '../review-form';
import styles from './reviews.module.scss';

type ReviewsProps = {
  reviews: Review[];
  expandedReviews: Set<number>;
  onToggleReview: (id: number) => void;
  highlightedId?: number | null;
  variant?: 'team' | 'reader';
};

const DeleteButton = ({ reviewId }: { reviewId: number }) => {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const deletion = useMutation({
    mutationFn: () => apiDeleteReview(reviewId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['reviews'] }),
  });

  if (!confirming) {
    return (
      <button
        type="button"
        className={styles.trash}
        onClick={() => setConfirming(true)}
        aria-label="Delete review"
        title="Delete review"
      >
        <TrashIcon size={16} />
      </button>
    );
  }

  return (
    <span className={styles.confirm}>
      {deletion.isError ? 'Could not delete' : 'Delete?'}
      <button
        type="button"
        className={styles.confirmNo}
        disabled={deletion.isPending}
        onClick={() => {
          deletion.reset();
          setConfirming(false);
        }}
      >
        Cancel
      </button>
      <button
        type="button"
        className={styles.confirmYes}
        disabled={deletion.isPending}
        onClick={() => deletion.mutate()}
      >
        {deletion.isPending ? 'Deleting…' : 'Delete'}
      </button>
    </span>
  );
};

export const Reviews = ({
  reviews,
  expandedReviews,
  onToggleReview,
  highlightedId,
  variant = 'team',
}: ReviewsProps) => {
  const { isAdmin } = useAuth();
  const [editingId, setEditingId] = useState<number | null>(null);

  return (
    <div className={cn(styles.reviews, reviews.length > 1 && styles.grid)}>
      {reviews.map((review) => {
        if (isAdmin && editingId === review.id) {
          return (
            <ReviewForm
              key={review.id}
              bookId={review.bookId}
              review={review}
              onCancel={() => setEditingId(null)}
              onSaved={() => setEditingId(null)}
            />
          );
        }

        const isLong = review.text.length > 1000;
        const isExpanded = expandedReviews.has(review.id);
        const displayText =
          isLong && !isExpanded
            ? review.text.slice(0, 1000) + '...'
            : review.text;

        return (
          <div
            key={review.id}
            id={`review-${review.id}`}
            className={cn(
              styles.reviewCard,
              highlightedId === review.id && styles.highlighted,
              variant === 'reader' && styles.reader,
            )}
          >
            <div className={styles.reviewHeader}>
              <div className={styles.avatar}>{review.authorInitials}</div>
              <div className={styles.reviewMeta}>
                <Link
                  className={styles.reviewAuthor}
                  to={`/reviews?author=${encodeURIComponent(review.authorName)}`}
                  title={`All reviews by ${review.authorName}`}
                >
                  {review.authorName}
                </Link>
                <span className={styles.reviewDate}>{review.date}</span>
              </div>
              <StarRating rating={review.rating} outOf={10} size={13} />
              {isAdmin && variant === 'team' && (
                <EditButton
                  onClick={() => setEditingId(review.id)}
                  label="Edit review"
                  size={16}
                />
              )}
              {isAdmin && variant === 'reader' && (
                <DeleteButton reviewId={review.id} />
              )}
            </div>
            <p className={styles.reviewText}>{displayText}</p>
            {isLong && (
              <button
                className={styles.readMore}
                onClick={() => onToggleReview(review.id)}
              >
                {isExpanded ? 'Show less' : 'Read more'}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};
